import { Storage } from "aws-amplify";
import moment from "moment-timezone";
import uuidv4 from "uuid/v4";

import {
  getPropertyEventsForInstance,
  sendReportEmails,
} from "./appsyncOperations";
import config from "../config";
import { moment as momentConst } from "../constants";

const pdfMake = require('pdfmake/build/pdfmake');
const pdfFonts = require('pdfmake/build/vfs_fonts');

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const reader = (file) => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = () => resolve(fileReader.result);
    fileReader.readAsDataURL(file);
  });
};

const createCoverPageContent = async ({
  endDate,
  serviceProvider,
  startDate,
}) => {
  const content = [];
  const toc = {
    toc: {
      id: "mainToc",
      title: {
        text: "Report Overview",
        style: "header",
        margin: [0, 64, 0, 0],
      },
    },
  };
  content.push({
    width: "*",
    style: "title",
    alignment: "center",
    text: serviceProvider.fullName,
  });
  content.push({
    alignment: "center",
    style: "dateRange",
    text: `${moment(startDate).format(momentConst.format)}to ${moment(
      endDate
    ).format(momentConst.format)}`,
    width: "*",
  });
  try {
    const url = await Storage.get(serviceProvider.logoPath, {
      expires: 60,
      bucket: config.s3.spBucket,
    });
    const response = await fetch(url);
    if (response.ok) {
      const blob = await response.blob();
      content.push({
        image: await reader(blob),
        width: 150,
        alignment: "center",
        style: "picture",
      });
    }
  } catch (error) {
    console.log(error);
  }
  return { content, toc };
};

const createAttachedMediaContent = async ({ attachedMedia, timezone }) => {
  const content = [];
  if (!(attachedMedia && attachedMedia.length > 0)) {
    return content;
  }
  for (const media of attachedMedia) {
    try {
      const url = await Storage.get(media.mediaId, { expires: 60 });
      const response = await fetch(url);
      const blob = await response.blob();
      content.push({
        alignment: "center",
        image: await reader(blob),
        style: "picture",
        width: 450,
      });
      if (media.createdAt) {
        content.push({
          style: "pictureTimestamp",
          text: moment(media.createdAt).tz(timezone).format(momentConst.format),
        });
      }
      if (media.description) {
        content.push({
          style: "pictureDescription",
          text: media.description,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }
  return content;
};

const createDarsContent = async ({
  client,
  propertiesById,
  reportsByPropertyId,
}) => {
  const content = [];
  let toc = null;
  for (const propertyId of client.propertyIds) {
    if (reportsByPropertyId[propertyId]) {
      const dars = Object.values(reportsByPropertyId[propertyId].dars);
      if (dars.length > 0) {
        const property = propertiesById[propertyId];
        if (toc === null) {
          toc = {
            toc: {
              id: "darToc",
              title: {
                style: "tocHeadingOne",
                text: "Daily Activity Reports",
              },
            },
          };
        }
        for (const dar of dars) {
          const body = [
            [
              {
                style: "tableHeader",
                text: "Report Information",
              },
              "",
            ],
            [
              {
                style: "tableItem",
                text: "Started At",
              },
              {
                style: "tableItem",
                text: moment(dar.startTime)
                  .tz(property.place.timezone)
                  .format(momentConst.format),
              },
            ],
            [
              {
                style: "tableItem",
                text: "Ended At",
              },
              {
                style: "tableItem",
                text: moment(dar.endTime)
                  .tz(property.place.timezone)
                  .format(momentConst.format),
              },
            ],
            [
              {
                style: "tableItem",
                text: "Officer Name",
              },
              {
                style: "tableItem",
                text: dar.displayName,
              },
            ],
            [
              {
                style: "tableItem",
                text: "Address",
              },
              {
                style: "tableItem",
                text: dar.formattedAddress,
              },
            ],
            [
              {
                style: "tableItem",
                text: "Notes",
              },
              {
                style: "tableItem",
                text: dar.notes,
              },
            ],
          ];
          if (dar.isVacationWatch) {
            body.push([
              {
                style: "tableItem",
                text: "VIP Report",
              },
              {
                style: "tableItem",
                text: dar.isVacationWatch,
              },
            ]);
          } else if (dar.isCardinal) {
            body.push([
              {
                style: "tableItem",
                text: "VIP Report",
              },
              {
                style: "tableItem",
                text: dar.isCardinal,
              },
            ]);
          }
          const header = {
            pageBreak: "before",
            style: "header",
            text: property.displayName,
          };
          if (dars.indexOf(dar) === 0) {
            header.tocItem = "darToc";
            header.tocMargin = [16, 0, 4, 8];
          }
          content.push(header);
          content.push({
            style: "subheader",
            text: "Daily Activity Report",
          });
          content.push({
            fillColor: "#000000",
            fillOpacity: 0.08,
            layout: "lightHorizontalLines",
            style: "table",
            table: {
              body,
              headerRows: 1,
              widths: ["*", "*"],
            },
            width: "*",
          });
          if (dar.attachedMedia && dar.attachedMedia.length > 0) {
            const media = await createAttachedMediaContent({
              attachedMedia: dar.attachedMedia,
              timezone: property.place.timezone,
            });
            content.push(...media);
          }
        }
      }
    }
  }
  return { content, toc };
};

const createResponseReportContent = async ({ responseId, property }) => {
  const content = [];
  const events = await getPropertyEventsForInstance(responseId);
  content.push({
    fillColor: "#000000",
    fillOpacity: 0.08,
    layout: "lightHorizontalLines",
    style: "table",
    table: {
      body: [
        [{ style: "tableHeader", text: "Report Information" }, ""],
        [
          { style: "tableItem", text: "Address" },
          { style: "tableItem", text: property.place.formattedAddress },
        ],
      ],
      headerRows: 1,
      widths: ["*", "*"],
    },
  });
  const rows = [
    [
      { style: "tableHeader", text: "Time" },
      { style: "tableHeader", text: "Name" },
      { style: "tableHeader", text: "Message" },
    ],
  ];
  for (const event of events) {
    rows.push([
      {
        style: "tableItem",
        text: moment(event.createdAt)
          .tz(property.place.timezone)
          .format(momentConst.format),
      },
      { style: "tableItem", text: event.senderName },
      { style: "tableItem", text: event.text },
    ]);
  }
  content.push({
    style: "table",
    table: {
      body: rows,
      headerRows: 1,
      widths: ["35%", "15%", "50%"],
    },
  });
  return content;
};

const createResponseReportsContent = async ({
  client,
  propertiesById,
  reportsByPropertyId,
}) => {
  const content = [];
  let toc = null;
  for (const propertyId of client.propertyIds) {
    if (reportsByPropertyId[propertyId]) {
      const responses = Object.values(
        reportsByPropertyId[propertyId].responses
      );
      if (responses.length > 0) {
        const property = propertiesById[propertyId];
        if (toc === null) {
          toc = {
            toc: {
              id: "responseToc",
              title: {
                text: "Responses",
                style: "tocHeadingOne",
              },
            },
          };
        }
        for (const response of responses) {
          const header = {
            pageBreak: "before",
            style: "header",
            text: property.displayName,
          };
          if (responses.indexOf(response) === 0) {
            header.tocItem = "responseToc";
            header.tocMargin = [16, 0, 4, 8];
          }
          content.push(header);
          content.push({
            style: "subheader",
            text: "Response Report",
          });
          const responseReportContent = await createResponseReportContent({
            responseId: response.instanceId,
            property,
          });
          content.push(...responseReportContent);
        }
      }
    }
  }
  return { content, toc };
};

const getValue = (input, callback = (input) => input) =>
  input ? callback(input) : "N/A";

const createIncidentReportsContent = async ({
  client,
  propertiesById,
  reportsByPropertyId,
}) => {
  const content = [];
  let toc = null;
  for (const propertyId of client.propertyIds) {
    if (reportsByPropertyId[propertyId]) {
      const incidentReports = Object.values(
        reportsByPropertyId[propertyId].incidentReports
      );
      if (incidentReports.length > 0) {
        const property = propertiesById[propertyId];
        if (toc === null) {
          toc = {
            toc: {
              id: "incidentToc",
              title: {
                style: "tocHeadingOne",
                text: "Incident Reports",
              },
            },
          };
        }
        for (const incidentReport of incidentReports) {
          const header = {
            pageBreak: "before",
            style: "header",
            text: property.displayName,
          };
          if (incidentReports.indexOf(incidentReport) === 0) {
            header.tocItem = "incidentToc";
            header.tocMargin = [16, 0, 4, 8];
          }
          content.push(header);
          content.push({
            style: "subheader",
            text: "Incident Report",
          });
          content.push({
            fillColor: "#000000",
            fillOpacity: 0.08,
            headerRows: 1,
            layout: "lightHorizontalLines",
            style: "table",
            table: {
              body: [
                [{ style: "tableHeader", text: "Report Information" }, ""],
                [
                  { style: "tableItem", text: "Started At" },
                  {
                    style: "tableItem",
                    text: getValue(incidentReport.startTime, (time) =>
                      moment(time)
                        .tz(property.place.timezone)
                        .format(momentConst.format)
                    ),
                  },
                ],
                [
                  { style: "tableItem", text: "Ended At" },
                  {
                    style: "tableItem",
                    text: getValue(incidentReport.endTime, (time) =>
                      moment(time)
                        .tz(property.place.timezone)
                        .format(momentConst.format)
                    ),
                  },
                ],
                [
                  { style: "tableItem", text: "Title" },
                  { style: "tableItem", text: getValue(incidentReport.title) },
                ],
                [
                  { style: "tableItem", text: "Address" },
                  { style: "tableItem", text: property.place.formattedAddress },
                ],
                [
                  { style: "tableItem", text: "Submitted By" },
                  { style: "tableItem", text: incidentReport.submitted.by },
                ],
                [
                  { style: "tableItem", text: "Submitted At" },
                  {
                    style: "tableItem",
                    text: moment(incidentReport.submitted.at)
                      .tz(property.place.timezone)
                      .format(momentConst.format),
                  },
                ],
                [
                  { style: "tableItem", text: "Description" },
                  {
                    style: "tableItem",
                    text: getValue(incidentReport.description),
                  },
                ],
              ],
              widths: ["*", "*"],
            },
          });
          if (incidentReport.connectedInstance) {
            content.push({ text: "Attached Response", style: "subheader" });
            const responseReportContent = await createResponseReportContent({
              property,
              responseId: incidentReport.connectedInstance,
            });
            content.push(...responseReportContent);
          }
          if (incidentReport.policeCase) {
            content.push({ style: "subheaderTwo", text: "Police Case" });
            content.push({
              fillColor: "#000000",
              fillOpacity: 0.08,
              layout: "lightHorizontalLines",
              style: "table",
              table: {
                body: [
                  [
                    { style: "tableHeader", text: "Police Case Information" },
                    "",
                  ],
                  [
                    { style: "tableItem", text: "Agency" },
                    {
                      style: "tableItem",
                      text: incidentReport.policeCase.agency,
                    },
                  ],
                  [
                    { style: "tableItem", text: "Case Number" },
                    {
                      style: "tableItem",
                      text: incidentReport.policeCase.number,
                    },
                  ],
                ],
                headerRows: 1,
                widths: ["*", "*"],
              },
            });
          }
          if (incidentReport.subjects) {
            content.push({ style: "subheaderTwo", text: "Subjects" });
            for (const subject of incidentReport.subjects) {
              content.push({
                fillColor: "#000000",
                fillOpacity: 0.08,
                layout: "lightHorizontalLines",
                style: "table",
                table: {
                  body: [
                    [{ style: "tableHeader", text: "Subject Information" }, ""],
                    [
                      { style: "tableItem", text: "Name" },
                      { style: "tableItem", text: subject.name },
                    ],
                    [
                      { style: "tableItem", text: "Type" },
                      { style: "tableItem", text: getValue(subject.type) },
                    ],
                    [
                      { style: "tableItem", text: "Sex" },
                      { style: "tableItem", text: getValue(subject.sex) },
                    ],
                    [
                      { style: "tableItem", text: "Ethnicity" },
                      { style: "tableItem", text: getValue(subject.ethnicity) },
                    ],
                    [
                      { style: "tableItem", text: "Race" },
                      { style: "tableItem", text: getValue(subject.race) },
                    ],
                    [
                      { style: "tableItem", text: "Phone Number" },
                      {
                        style: "tableItem",
                        text: getValue(subject.phoneNumber),
                      },
                    ],
                    [
                      { style: "tableItem", text: "Address" },
                      { style: "tableItem", text: getValue(subject.address) },
                    ],
                    [
                      { style: "tableItem", text: "Description" },
                      {
                        style: "tableItem",
                        text: getValue(subject.description),
                      },
                    ],
                    [
                      { style: "tableItem", text: "Height" },
                      { style: "tableItem", text: getValue(subject.height) },
                    ],
                    [
                      { style: "tableItem", text: "Weight" },
                      { style: "tableItem", text: getValue(subject.weight) },
                    ],
                    [
                      { style: "tableItem", text: "Date Of Birth" },
                      {
                        style: "tableItem",
                        text: getValue(subject.dateOfBirth),
                      },
                    ],
                    [
                      { style: "tableItem", text: "Age" },
                      { style: "tableItem", text: getValue(subject.age) },
                    ],
                    [
                      { style: "tableItem", text: "Hair Color" },
                      { style: "tableItem", text: getValue(subject.hairColor) },
                    ],
                    [
                      { style: "tableItem", text: "Eye Color" },
                      { style: "tableItem", text: getValue(subject.eyeColor) },
                    ],
                    [
                      { style: "tableItem", text: "License" },
                      {
                        style: "tableItem",
                        text: getValue(
                          subject.license,
                          (license) =>
                            `State: ${license.state}, Number: ${license.number}`
                        ),
                      },
                    ],
                  ],
                  headerRows: 1,
                  widths: ["*", "*"],
                },
              });
            }
          }
          if (incidentReport.vehicles) {
            content.push({ style: "subheaderTwo", text: "Vehicles" });
            for (const vehicle of incidentReport.vehicles) {
              content.push({
                fillColor: "#000000",
                fillOpacity: 0.08,
                layout: "lightHorizontalLines",
                style: "table",
                table: {
                  body: [
                    [{ style: "tableHeader", text: "Vehicle Information" }, ""],
                    [
                      { style: "tableItem", text: "Type" },
                      { style: "tableItem", text: getValue(vehicle.type) },
                    ],
                    [
                      { style: "tableItem", text: "Description" },
                      {
                        style: "tableItem",
                        text: getValue(vehicle.description),
                      },
                    ],
                    [
                      { style: "tableItem", text: "Make" },
                      { style: "tableItem", text: getValue(vehicle.make) },
                    ],
                    [
                      { style: "tableItem", text: "Model" },
                      { style: "tableItem", text: getValue(vehicle.model) },
                    ],
                    [
                      { style: "tableItem", text: "Year" },
                      { style: "tableItem", text: getValue(vehicle.year) },
                    ],
                    [
                      { style: "tableItem", text: "Color" },
                      { style: "tableItem", text: getValue(vehicle.color) },
                    ],
                    [
                      { style: "tableItem", text: "Vin" },
                      { style: "tableItem", text: getValue(vehicle.vin) },
                    ],
                    [
                      { style: "tableItem", text: "Registered Owner" },
                      {
                        style: "tableItem",
                        text: getValue(vehicle.registeredOwner),
                      },
                    ],
                    [
                      { style: "tableItem", text: "Status" },
                      { style: "tableItem", text: getValue(vehicle.status) },
                    ],
                    [
                      { style: "tableItem", text: "License" },
                      {
                        style: "tableItem",
                        text: getValue(
                          vehicle.vehicleLicense,
                          (license) =>
                            `State: ${license.state}, Number: ${license.number}`
                        ),
                      },
                    ],
                  ],
                  headerRows: 1,
                  widths: ["*", "*"],
                },
              });
            }
          }
          if (incidentReport.attachedMedia) {
            const media = await createAttachedMediaContent({
              attachedMedia: incidentReport.attachedMedia,
              timezone: property.place.timezone,
            });
            content.push(...media);
          }
        }
      }
    }
  }
  return { content, toc };
};

const createPdfContent = async ({
  client,
  endDate,
  propertiesById,
  reportsByPropertyId,
  serviceProvider,
  startDate,
}) => {
  const toc = [];
  const {
    content: coverPageContent,
    toc: coverPageToc,
  } = await createCoverPageContent({
    endDate,
    serviceProvider,
    startDate,
  });
  toc.push(coverPageToc);
  const { content: darsContent, toc: darsToc } = await createDarsContent({
    client,
    propertiesById,
    reportsByPropertyId,
  });
  if (darsToc) {
    toc.push(darsToc);
  }
  const {
    content: incidentsContent,
    toc: incidentsToc,
  } = await createIncidentReportsContent({
    client,
    propertiesById,
    reportsByPropertyId,
  });
  if (incidentsToc) {
    toc.push(incidentsToc);
  }
  const {
    content: responsesContent,
    toc: responsesToc,
  } = await createResponseReportsContent({
    client,
    propertiesById,
    reportsByPropertyId,
  });
  if (responsesToc) {
    toc.push(responsesToc);
  }
  return [
    ...coverPageContent,
    ...toc,
    ...darsContent,
    ...incidentsContent,
    ...responsesContent,
  ];
};

const uploadPdf = async (pdf) => {
  const { key } = await new Promise((resolve, reject) => {
    pdf.getBlob((blob) => {
      Storage.vault
        .put(`report-${uuidv4()}.pdf`, blob, {
          acl: "public-read",
          bucket: config.s3.reportsBucket,
          contentType: "application/pdf",
          level: "public",
        })
        .then((result) => resolve(result))
        .catch((error) => reject(error));
    });
  });
  return `public/${key}`;
};

const createAndUploadReportPdf = async ({
  client,
  endDate,
  propertiesById,
  reportsByPropertyId,
  serviceProvider,
  startDate,
}) => {
  const pdf = pdfMake.createPdf({
    content: await createPdfContent({
      client,
      endDate,
      propertiesById,
      reportsByPropertyId,
      serviceProvider,
      startDate,
    }),
    defaultStyle: {
      font: "Roboto",
    },
    footer: (currentPage, pageCount) => {
      if (currentPage === 1) {
        return null;
      }
      return {
        alignment: "center",
        fontSize: 10,
        margin: 16,
        text: `${currentPage} of ${pageCount}`,
      };
    },
    header: (currentPage) => {
      if (currentPage === 1) {
        return null;
      }
      return {
        alignment: "center",
        bold: true,
        fontSize: 10,
        margin: 16,
        text: serviceProvider.fullName,
      };
    },
    pageBreakBefore: (currentNode, followingNodesOnPage) => {
      return (
        currentNode.startPosition.pageNumber !== 1 &&
        currentNode.image &&
        followingNodesOnPage.length <= 3
      );
    },
    styles: {
      dateRange: {
        margin: [0, 0, 0, 8],
      },
      header: {
        fontSize: 18,
        bold: true,
        margin: [4, 0],
      },
      picture: {
        margin: [0, 8, 0, 8],
      },
      pictureDescription: {
        margin: [50, 0, 50, 8],
      },
      pictureTimestamp: {
        bold: true,
        fontSize: 10,
        margin: [50, 0, 50, 4],
      },
      title: {
        fontSize: 24,
        bold: true,
        margin: [0, 0, 0, 8],
      },
      table: {
        margin: [0, 8, 0, 16],
      },
      tableHeader: {
        bold: true,
        margin: [4, 8],
      },
      tableItem: {
        margin: [4, 8],
      },
      subheader: {
        fontSize: 14,
        margin: [4, 0, 4, 8],
      },
      subheaderTwo: {
        fontSize: 14,
        margin: [4, 8, 4, 0],
      },
      tocHeadingOne: {
        margin: [0, 8],
      },
    },
  });
  const key = await uploadPdf(pdf);
  return { clientId: client.id, url: key };
};

const sendReports = async ({
  clients,
  dars = [],
  endDate,
  incidents = [],
  propertiesById,
  responses = [],
  serviceProvider,
  startDate,
}) => {
  const reportsByPropertyId = {};
  for (const incidentReport of incidents) {
    const { incidentId, propertyId } = incidentReport;
    if (reportsByPropertyId[propertyId]) {
      reportsByPropertyId[propertyId] = {
        ...reportsByPropertyId[propertyId],
        incidentReports: {
          ...reportsByPropertyId[propertyId].incidentReports,
          [incidentId]: incidentReport,
        },
      };
    } else {
      reportsByPropertyId[propertyId] = {
        dars: {},
        incidentReports: {
          [incidentId]: incidentReport,
        },
        responses: {},
      };
    }
  }
  for (const dar of dars) {
    const { patrolId, propertyId } = dar;
    if (reportsByPropertyId[propertyId]) {
      reportsByPropertyId[propertyId] = {
        ...reportsByPropertyId[propertyId],
        dars: {
          ...reportsByPropertyId[propertyId].dars,
          [patrolId]: dar,
        },
      };
    } else {
      reportsByPropertyId[propertyId] = {
        dars: {
          [patrolId]: dar,
        },
        incidentReports: {},
        responses: {},
      };
    }
  }
  for (const response of responses) {
    const { instanceId, propertyId } = response;
    if (reportsByPropertyId[propertyId]) {
      reportsByPropertyId[propertyId] = {
        ...reportsByPropertyId[propertyId],
        responses: {
          ...reportsByPropertyId[propertyId].responses,
          [instanceId]: response,
        },
      };
    } else {
      reportsByPropertyId[propertyId] = {
        dars: {},
        incidentReports: {},
        responses: {
          [instanceId]: response,
        },
      };
    }
  }
  const propertyIdsWithReports = Object.keys(reportsByPropertyId);
  const promiseArray = [];
  clients.forEach((client) => {
    if (!client.isArchived && !client.isDeleted) {
      let clientNeedsToBeEmailed = false;
      for (const propertyId of client.propertyIds) {
        if (propertyIdsWithReports.includes(propertyId)) {
          clientNeedsToBeEmailed = true;
        }
      }
      if (clientNeedsToBeEmailed) {
        promiseArray.push(
          createAndUploadReportPdf({
            client,
            endDate,
            propertiesById,
            reportsByPropertyId,
            serviceProvider,
            startDate,
          })
        );
      }
    }
  });
  const input = await Promise.all(promiseArray);
  return await sendReportEmails({
    serviceProviderId: serviceProvider.serviceProviderId,
    input,
  });
};

export default sendReports;
